import { useEffect, useRef, useState } from 'react';

// --- Intersection Observer hook for scroll-triggered reveals ---
const useReveal = (threshold = 0.15) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.unobserve(el); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
};

// Stagger children reveal — applies fade-in to container, children animate via CSS
const RevealGroup = ({ children, className, style }) => {
  const [ref, visible] = useReveal(0.1);
  return (
    <div
      ref={ref}
      className={className}
      style={{
        ...style,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(30px)',
        transition: 'opacity 0.7s ease, transform 0.7s ease',
      }}
    >
      {children}
    </div>
  );
};

// --- Animated counter ---
const AnimatedNumber = ({ target, suffix = '', duration = 1200 }) => {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const start = performance.now();
        const tick = (now) => {
          const progress = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setValue(Math.round(eased * target));
          if (progress < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
        obs.unobserve(el);
      }
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [target, duration]);

  return <span ref={ref}>{value.toLocaleString()}{suffix}</span>;
};

const DEMO_DATA = {
  videoTitle: 'How I Built a $10M Business From My Bedroom',
  videoId: 'dQw4w9WgXcQ',
  duration: '14:32',
  words: '3,847',
  singles: [
    { word: 'business', count: 47 },
    { word: 'revenue', count: 31 },
    { word: 'product', count: 28 },
    { word: 'customers', count: 24 },
    { word: 'marketing', count: 21 },
    { word: 'growth', count: 18 },
  ],
  aiTitles: [
    'I Made $10M Without Leaving My Room',
    'The Bedroom Business Blueprint Nobody Talks About',
    'From Zero to $10M: What I Wish I Knew Sooner',
  ],
  sentiment: {
    overall: 'positive',
    tone: 'motivational and candid',
    energy: 'high',
    score: 82,
    summary: 'An energetic, personal story with honest reflections on failures and breakthroughs.',
  },
  aiQuotes: [
    'Revenue means nothing if your customers don\'t come back',
    'I failed six times before one product actually worked',
    'The best marketing is a product people can\'t stop talking about',
  ],
  hooks: [
    'What if I told you the biggest business decision I ever made happened in my pajamas?',
    'Six failed products. Zero revenue. And then everything changed.',
  ],
};

const SIM_DEMO_DATA = {
  titles: [
    { title: 'I Spent 50 Hours Buried Alive', ctr: 9.4, rating: 'Excellent', color: '#44dd66' },
    { title: 'Being Buried Underground for 50 Hours', ctr: 3.1, rating: 'Good', color: '#ccaa22' },
    { title: '50 Hour Burial Challenge', ctr: 4.7, rating: 'Good', color: '#ccaa22' },
  ],
  archetypes: [
    { name: 'Impulse Clicker', ctr1: 14.2, ctr2: 4.8, ctr3: 7.1 },
    { name: 'Casual Scroller', ctr1: 8.7, ctr2: 2.9, ctr3: 4.3 },
    { name: 'Knowledge Seeker', ctr1: 6.1, ctr2: 3.4, ctr3: 4.9 },
    { name: 'Skeptic', ctr1: 4.3, ctr2: 2.1, ctr3: 3.0 },
  ],
  strengths: ['Curiosity gap', 'Specificity', 'Emotional pull', 'Narrative hook'],
  weaknesses: ['No social proof', 'Low authority signal'],
};

const ANIM_CSS = `
@keyframes float1 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(30px, -40px) scale(1.05); }
  66% { transform: translate(-20px, 20px) scale(0.95); }
}
@keyframes float2 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(-40px, 30px) scale(1.08); }
  66% { transform: translate(25px, -25px) scale(0.92); }
}
@keyframes float3 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(20px, -30px) scale(1.04); }
}
@keyframes pulse-glow {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}
@keyframes hero-fade-in {
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes hero-fade-in-delay {
  0%, 25% { opacity: 0; transform: translateY(20px); }
  100% { opacity: 1; transform: translateY(0); }
}
@keyframes hero-fade-in-delay2 {
  0%, 45% { opacity: 0; transform: translateY(16px); }
  100% { opacity: 1; transform: translateY(0); }
}
@keyframes shimmer {
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
}
@keyframes bar-grow {
  from { width: 0%; }
}
.landing-hero-title-anim {
  animation: hero-fade-in 1s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}
.landing-hero-sub-anim {
  animation: hero-fade-in-delay 1.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}
.landing-hero-cta-anim {
  animation: hero-fade-in-delay2 1.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}
.landing-stat-card:hover {
  transform: translateY(-4px) !important;
  border-color: rgba(124,58,237,0.4) !important;
  box-shadow: 0 8px 40px rgba(124,58,237,0.15) !important;
}
.landing-feature-card:hover {
  transform: translateY(-6px) !important;
  border-color: rgba(124,58,237,0.3) !important;
  box-shadow: 0 12px 48px rgba(124,58,237,0.12) !important;
}
.landing-feature-card-ai:hover {
  transform: translateY(-6px) !important;
  border-color: rgba(16, 185, 129, 0.4) !important;
  box-shadow: 0 12px 48px rgba(16, 185, 129, 0.12) !important;
}
.landing-cta-btn:hover {
  transform: scale(1.04) !important;
  box-shadow: 0 8px 40px rgba(124,58,237,0.4) !important;
}
.landing-cta-btn:active {
  transform: scale(0.98) !important;
}
@media (max-width: 768px) {
  .landing-hero-title-text { font-size: 36px !important; letter-spacing: -1.5px !important; }
  .landing-hero-sub-text { font-size: 16px !important; }
  .landing-stats-row { flex-direction: column !important; gap: 12px !important; }
  .landing-steps-row { flex-direction: column !important; align-items: center !important; }
  .landing-step-arrow { transform: rotate(90deg) !important; }
  .landing-features-grid { grid-template-columns: 1fr !important; }
  .landing-demo-grid { grid-template-columns: 1fr !important; }
  .landing-why-grid { grid-template-columns: 1fr !important; }
  .landing-orb { display: none !important; }
}
`;

const DemoBar = ({ word, count, max, visible, delay }) => (
  <div style={demoStyles.barRow}>
    <span style={demoStyles.barWord}>{word}</span>
    <div style={demoStyles.barTrack}>
      <div style={{
        ...demoStyles.barFill,
        width: visible ? `${(count / max) * 100}%` : '0%',
        transition: `width 0.8s ease ${delay}s`,
      }} />
    </div>
    <span style={demoStyles.barCount}>{count}</span>
  </div>
);

const LandingPage = ({ onGetStarted, onSignIn }) => {
  const [demoRef, demoVisible] = useReveal(0.1);
  const [activeFeature, setActiveFeature] = useState(0);

  return (
    <div style={styles.page}>
      <style>{ANIM_CSS}</style>

      {/* Floating gradient orbs */}
      <div className="landing-orb" style={styles.orb1} />
      <div className="landing-orb" style={styles.orb2} />
      <div className="landing-orb" style={styles.orb3} />

      {/* ===== HERO ===== */}
      <section style={styles.hero}>
        <div className="landing-hero-title-anim">
          <h1 style={styles.heroTitle} className="landing-hero-title-text">
            Research. Optimize.<br />Test before you publish.
          </h1>
        </div>
        <div className="landing-hero-sub-anim">
          <p style={styles.heroSub} className="landing-hero-sub-text">
            Analyze any YouTube video for keywords, AI-generated titles, and SEO insights — then test your titles against 100K simulated viewers before publishing. Built for creators who want more views.
          </p>
        </div>
        <div className="landing-hero-cta-anim">
          <button onClick={onSignIn || onGetStarted} style={styles.ctaButton} className="landing-cta-btn">
            Get Started Free
          </button>
          <p style={styles.ctaHint}>3 free AI credits. No credit card required.</p>
        </div>

        {/* Stats row */}
        <RevealGroup className="landing-stats-row" style={styles.statsRow}>
          <div style={styles.statCard} className="landing-stat-card">
            <span style={styles.statValue}><AnimatedNumber target={12} suffix="+" /></span>
            <span style={styles.statLabel}>Analysis features</span>
          </div>
          <div style={styles.statCard} className="landing-stat-card">
            <span style={styles.statValue}><AnimatedNumber target={5} suffix="s" /></span>
            <span style={styles.statLabel}>Average analysis time</span>
          </div>
          <div style={styles.statCard} className="landing-stat-card">
            <span style={styles.statValue}><AnimatedNumber target={100} suffix="%" /></span>
            <span style={styles.statLabel}>Content-based results</span>
          </div>
        </RevealGroup>
      </section>

      {/* ===== PRODUCT FEATURES TABS ===== */}
      <section style={styles.section}>
        <SectionHeader
          title="Built for creators"
          subtitle="Powerful features to research, optimize, and test your content"
        />
        <FeatureTabs onSignIn={onSignIn || onGetStarted} activeTab={activeFeature} setActiveTab={setActiveFeature} />
      </section>

      {/* ===== LIVE DEMO ===== */}
      <section style={styles.section}>
        <SectionHeader
          title="See it in action"
          subtitle={activeFeature === 0 ? 'Real analysis output from a single video' : 'Simulated CTR results from three title variations'}
        />
        <div style={styles.demoContainer} ref={demoRef}>
          {activeFeature === 0 ? (
            <>
              {/* Demo video card */}
              <div style={styles.demoVideoCard}>
                <div style={styles.demoThumbPlaceholder}>
                  <svg viewBox="0 0 24 24" width="40" height="40" fill="rgba(255,255,255,0.3)">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 style={styles.demoVideoTitle}>{DEMO_DATA.videoTitle}</h3>
                  <div style={styles.demoVideoStats}>
                    <span>Duration: {DEMO_DATA.duration}</span>
                    <span>Words: {DEMO_DATA.words}</span>
                  </div>
                </div>
              </div>

              {/* Demo results grid */}
              <div className="landing-demo-grid" style={styles.demoGrid}>
                {/* AI Titles */}
                <div style={demoStyles.aiCard}>
                  <div style={demoStyles.aiLabel}>AI</div>
                  <h4 style={demoStyles.cardTitle}>Title Suggestions</h4>
                  {DEMO_DATA.aiTitles.map((t, i) => (
                    <div key={i} style={{
                      ...demoStyles.titleIdea,
                      opacity: demoVisible ? 1 : 0,
                      transform: demoVisible ? 'translateX(0)' : 'translateX(-20px)',
                      transition: `opacity 0.5s ease ${0.3 + i * 0.15}s, transform 0.5s ease ${0.3 + i * 0.15}s`,
                    }}>
                      {t}
                      <span style={demoStyles.titleLen}>{t.length} chars</span>
                    </div>
                  ))}
                </div>

                {/* Sentiment */}
                <div style={demoStyles.aiCard}>
                  <div style={demoStyles.aiLabel}>AI</div>
                  <h4 style={demoStyles.cardTitle}>Sentiment Analysis</h4>
                  <div style={demoStyles.sentimentRow}>
                    <span style={demoStyles.sentimentChip}>
                      <span style={{ fontSize: '18px' }}>😊</span>
                      <span style={{ fontWeight: '600', color: '#e2e8f0' }}>{DEMO_DATA.sentiment.overall}</span>
                    </span>
                    <span style={demoStyles.toneChip}>{DEMO_DATA.sentiment.tone}</span>
                    <span style={demoStyles.energyChip}>⚡ {DEMO_DATA.sentiment.energy}</span>
                  </div>
                  {/* Sentiment meter */}
                  <div style={{ marginTop: '16px' }}>
                    <div style={demoStyles.meterTrack}>
                      <div style={{
                        height: '100%', borderRadius: '4px',
                        background: 'linear-gradient(90deg, #4ade80, #22c55e)',
                        width: demoVisible ? `${DEMO_DATA.sentiment.score}%` : '0%',
                        transition: 'width 1.2s ease 0.5s',
                      }} />
                    </div>
                    <div style={demoStyles.meterLabels}>
                      <span>Negative</span><span>Neutral</span><span>Positive</span>
                    </div>
                  </div>
                  <p style={demoStyles.sentimentSummary}>{DEMO_DATA.sentiment.summary}</p>
                </div>

                {/* Keywords */}
                <div style={demoStyles.card}>
                  <h4 style={demoStyles.cardTitle}>Top Keywords</h4>
                  {DEMO_DATA.singles.slice(0, 5).map((s, i) => (
                    <DemoBar key={s.word} word={s.word} count={s.count}
                      max={DEMO_DATA.singles[0].count} visible={demoVisible} delay={0.2 + i * 0.1} />
                  ))}
                </div>

                {/* Quotes */}
                <div style={demoStyles.aiCard}>
                  <div style={demoStyles.aiLabel}>AI</div>
                  <h4 style={demoStyles.cardTitle}>Best Quotes</h4>
                  {DEMO_DATA.aiQuotes.map((q, i) => (
                    <div key={i} style={{
                      ...demoStyles.aiQuote,
                      opacity: demoVisible ? 1 : 0,
                      transform: demoVisible ? 'translateX(0)' : 'translateX(20px)',
                      transition: `opacity 0.5s ease ${0.4 + i * 0.15}s, transform 0.5s ease ${0.4 + i * 0.15}s`,
                    }}>
                      "{q}"
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <SimDemoSection visible={demoVisible} />
          )}
        </div>
      </section>

      {/* ===== FEATURES ===== */}
      <section style={styles.section}>
        <SectionHeader
          title="Your creator toolkit"
          subtitle="Research, optimize, and test — everything you need to get more clicks on YouTube."
        />

        {/* Title Analyzer features */}
        <RevealGroup style={{ marginBottom: '20px' }}>
          <h3 style={styles.toolkitGroupTitle}>
            <svg viewBox="0 0 24 24" width="20" height="20" fill="#a78bfa"><path d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 0 0 1.48-5.34c-.47-2.78-2.79-5-5.59-5.34A6.505 6.505 0 0 0 3.03 10c.01 2.17 1.04 4.11 2.67 5.32l-.27.28v.79l-4.84 4.83c-.37.36-.37.96 0 1.32l.35.35c.37.37.96.37 1.32 0L7.09 18h.79l.28.27a6.515 6.515 0 0 0 5.34 1.48c2.78-.47 5-2.79 5.34-5.59a6.508 6.508 0 0 0-3.34-6.16zM9.5 14C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>
            Title Analyzer
          </h3>
        </RevealGroup>
        <RevealGroup className="landing-features-grid" style={styles.featuresGrid}>
          <FeatureCard
            title="YouTube Keyword Extractor"
            desc="Extract top keywords and phrases from any video transcript"
            icon={<svg viewBox="0 0 24 24" width="24" height="24" fill="#a78bfa"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zm6.93 6h-2.95a15.65 15.65 0 0 0-1.38-3.56A8.03 8.03 0 0 1 18.92 8zM12 4.04c.83 1.2 1.48 2.53 1.91 3.96h-3.82c.43-1.43 1.08-2.76 1.91-3.96zM4.26 14C4.1 13.36 4 12.69 4 12s.1-1.36.26-2h3.38c-.08.66-.14 1.32-.14 2s.06 1.34.14 2H4.26zm.82 2h2.95c.32 1.25.78 2.45 1.38 3.56A7.987 7.987 0 0 1 5.08 16zm2.95-8H5.08a7.987 7.987 0 0 1 4.33-3.56A15.65 15.65 0 0 0 8.03 8zM12 19.96c-.83-1.2-1.48-2.53-1.91-3.96h3.82c-.43 1.43-1.08 2.76-1.91 3.96zM14.34 14H9.66c-.09-.66-.16-1.32-.16-2s.07-1.35.16-2h4.68c.09.65.16 1.32.16 2s-.07 1.34-.16 2zm.25 5.56c.6-1.11 1.06-2.31 1.38-3.56h2.95a8.03 8.03 0 0 1-4.33 3.56zM16.36 14c.08-.66.14-1.32.14-2s-.06-1.34-.14-2h3.38c.16.64.26 1.31.26 2s-.1 1.36-.26 2h-3.38z"/></svg>}
          />
          <FeatureCard
            title="Readability Score"
            desc="Flesch-Kincaid readability grade to match your target audience"
            icon={<svg viewBox="0 0 24 24" width="24" height="24" fill="#a78bfa"><path d="M12 6.5a9.77 9.77 0 0 1 8.82 5.5c-1.65 3.37-5.02 5.5-8.82 5.5S4.83 15.37 3.18 12A9.77 9.77 0 0 1 12 6.5m0-2C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zm0 5a2.5 2.5 0 0 1 0 5 2.5 2.5 0 0 1 0-5m0-2c-2.48 0-4.5 2.02-4.5 4.5s2.02 4.5 4.5 4.5 4.5-2.02 4.5-4.5-2.02-4.5-4.5-4.5z"/></svg>}
          />
          <FeatureCard
            title="Power Word Detection"
            desc="Find the emotional, click-driving words that boost YouTube CTR"
            icon={<svg viewBox="0 0 24 24" width="24" height="24" fill="#a78bfa"><path d="M7 2v11h3v9l7-12h-4l4-8z"/></svg>}
          />
          <FeatureCard
            title="Topic Timeline"
            desc="Visualize when keywords appear throughout any video's content"
            icon={<svg viewBox="0 0 24 24" width="24" height="24" fill="#a78bfa"><path d="M3.5 18.49l6-6.01 4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 16.99z"/></svg>}
          />
          <FeatureCard pro
            title="AI Title Generator"
            desc="Generate click-worthy YouTube titles in 3 styles: curiosity, value, and emotional"
            icon={<svg viewBox="0 0 24 24" width="24" height="24" fill="#6ee7b7"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/></svg>}
          />
          <FeatureCard pro
            title="Description Generator"
            desc="Auto-generate optimized YouTube descriptions with SEO keywords and hashtags"
            icon={<svg viewBox="0 0 24 24" width="24" height="24" fill="#6ee7b7"><path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/></svg>}
          />
          <FeatureCard pro
            title="YouTube Transcript Analyzer"
            desc="Paste any YouTube URL to auto-fetch and analyze the full video transcript"
            icon={<svg viewBox="0 0 24 24" width="24" height="24" fill="#6ee7b7"><path d="M10 15l5.19-3L10 9v6m11.56-7.83c.13.47.22.97.22 1.52v9.62c0 .55-.09 1.05-.22 1.52-.12.47-.33.86-.61 1.16-.28.3-.63.52-1.07.65-.44.12-.96.19-1.57.19H5.69c-.61 0-1.13-.07-1.57-.19-.44-.13-.79-.35-1.07-.65-.28-.3-.49-.69-.61-1.16C2.31 19.36 2.22 18.86 2.22 18.31V8.69c0-.55.09-1.05.22-1.52.12-.47.33-.86.61-1.16.28-.3.63-.52 1.07-.65C4.56 5.24 5.08 5.17 5.69 5.17h12.62c.61 0 1.13.07 1.57.19.44.12.79.35 1.07.65.28.3.49.69.61 1.16z"/></svg>}
          />
          <FeatureCard pro
            title="Sentiment & Hook Analysis"
            desc="AI-powered mood analysis, best quotes, and opening hook ideas for better engagement"
            icon={<svg viewBox="0 0 24 24" width="24" height="24" fill="#6ee7b7"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z"/></svg>}
          />
        </RevealGroup>

        {/* CTR Simulator features */}
        <RevealGroup style={{ marginTop: '48px', marginBottom: '20px' }}>
          <h3 style={styles.toolkitGroupTitle}>
            <svg viewBox="0 0 24 24" width="20" height="20" fill="#fbbf24"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/></svg>
            CTR Simulator
          </h3>
        </RevealGroup>
        <RevealGroup className="landing-features-grid" style={styles.featuresGrid}>
          <FeatureCard pro
            title="100K Simulated Viewers"
            desc="Test titles against 100,000 synthetic viewers for statistically reliable results"
            icon={<svg viewBox="0 0 24 24" width="24" height="24" fill="#6ee7b7"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg>}
          />
          <FeatureCard pro
            title="9 Viewer Archetypes"
            desc="From Casual Scrollers to Deep Divers — each archetype clicks differently"
            icon={<svg viewBox="0 0 24 24" width="24" height="24" fill="#6ee7b7"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/></svg>}
          />
          <FeatureCard pro
            title="A/B/C Title Testing"
            desc="Compare up to 3 title variations with win counts and confidence intervals"
            icon={<svg viewBox="0 0 24 24" width="24" height="24" fill="#6ee7b7"><path d="M10 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h5v-2H5V5h5V3zm4 0v2h5v14h-5v2h5c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-5zm-3 8V7l-4 4 4 4v-4h6v-2h-6z"/></svg>}
          />
          <FeatureCard pro
            title="AI Rewrite Suggestions"
            desc="Get an improved title that addresses your weaknesses and maximizes CTR"
            icon={<svg viewBox="0 0 24 24" width="24" height="24" fill="#6ee7b7"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.001 1.001 0 0 0 0-1.41l-2.34-2.34a1.001 1.001 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>}
          />
        </RevealGroup>
      </section>

      {/* ===== WHY TITLER ===== */}
      <section style={styles.section}>
        <SectionHeader
          title="Why Titler?"
          subtitle="Research, optimize, and test — the full creator workflow in one platform"
        />
        <RevealGroup className="landing-why-grid" style={styles.whyGrid}>
          <div style={styles.whyCard} className="landing-feature-card">
            <div style={styles.whyIcon}>
              <svg viewBox="0 0 24 24" width="28" height="28" fill="#a78bfa"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
            </div>
            <h3 style={styles.whyTitle}>Research any video</h3>
            <p style={styles.whyDesc}>Analyze your own videos or study competitors. Extract keywords, hooks, and content structure from any YouTube URL.</p>
          </div>
          <div style={styles.whyCard} className="landing-feature-card">
            <div style={styles.whyIcon}>
              <svg viewBox="0 0 24 24" width="28" height="28" fill="#a78bfa"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/></svg>
            </div>
            <h3 style={styles.whyTitle}>Test before you publish</h3>
            <p style={styles.whyDesc}>Run your titles through 100K simulated viewers across 9 archetypes. See predicted CTR and pick the winner before going live.</p>
          </div>
          <div style={styles.whyCard} className="landing-feature-card">
            <div style={styles.whyIcon}>
              <svg viewBox="0 0 24 24" width="28" height="28" fill="#a78bfa"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/></svg>
            </div>
            <h3 style={styles.whyTitle}>Free to start, powerful to grow</h3>
            <p style={styles.whyDesc}>Basic analysis is always free. Pro unlocks AI titles, CTR simulation, descriptions, SEO tags, and saved history.</p>
          </div>
        </RevealGroup>
      </section>

      {/* ===== FINAL CTA ===== */}
      <FinalCTA onGetStarted={onGetStarted} onSignIn={onSignIn} />

      {/* Footer */}
      <footer style={styles.footer}>
        <span style={styles.footerText}>Titler — YouTube Title Research, Optimization & CTR Testing for Creators</span>
      </footer>
    </div>
  );
};

// --- Sub-components ---

const SectionHeader = ({ title, subtitle }) => {
  const [ref, visible] = useReveal(0.2);
  return (
    <div ref={ref} style={{
      textAlign: 'center', marginBottom: '48px',
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(30px)',
      transition: 'opacity 0.7s ease, transform 0.7s ease',
    }}>
      <h2 style={styles.sectionHeading}>{title}</h2>
      {subtitle && <p style={styles.sectionSub}>{subtitle}</p>}
    </div>
  );
};

const StepCard = ({ number, title, desc }) => (
  <div style={styles.step}>
    <div style={styles.stepNumber}>{number}</div>
    <h3 style={styles.stepTitle}>{title}</h3>
    <p style={styles.stepDesc}>{desc}</p>
  </div>
);

const FeatureCard = ({ title, desc, icon, pro }) => (
  <div style={pro ? styles.featureCardAi : styles.featureCard}
       className={pro ? 'landing-feature-card-ai' : 'landing-feature-card'}>
    <div style={pro ? styles.featureIconWrapAi : styles.featureIconWrap}>{icon}</div>
    {pro && <span style={styles.proBadge}>PRO</span>}
    <h4 style={pro ? styles.featureTitleAi : styles.featureTitle}>{title}</h4>
    <p style={styles.featureDesc}>{desc}</p>
  </div>
);

const FinalCTA = ({ onGetStarted, onSignIn }) => {
  const [ref, visible] = useReveal(0.2);
  return (
    <section ref={ref} style={{
      ...styles.finalCta,
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(40px)',
      transition: 'opacity 0.8s ease, transform 0.8s ease',
    }}>
      <h2 style={styles.finalCtaTitle}>Ready to get more YouTube views?</h2>
      <p style={styles.finalCtaSub}>
        Research competitors, generate AI titles, and test them against simulated viewers — all in one platform.
      </p>
      <button onClick={onSignIn || onGetStarted} style={styles.ctaButton} className="landing-cta-btn">
        Get Started Free
      </button>
      <p style={styles.ctaHint}>3 free AI credits. No credit card required.</p>
    </section>
  );
};

const FEATURE_TABS_DATA = [
  {
    id: 'analyzer',
    label: 'Title Analyzer',
    heading: 'Analyze any YouTube video in seconds',
    desc: 'Paste a YouTube URL or raw text to extract keywords, score readability, detect power words, and get AI-generated title alternatives.',
    link: '/youtube-title-analyzer/',
    appLink: '/#/app',
    ctaText: 'Try the Analyzer',
    steps: [
      { title: 'Paste any YouTube URL', desc: 'Analyze any video — your own or a competitor\'s. We auto-fetch the transcript for keyword extraction.' },
      { title: 'YouTube keyword analysis', desc: 'See top keywords, readability scores, power words, and topic timelines from any video\'s content.' },
      { title: 'Generate better titles', desc: 'Get AI title suggestions, SEO tags, video descriptions, and content ideas for your next video.' },
    ],
    points: [
      { title: 'Keyword Extraction', desc: 'Pull top keywords and phrases from any transcript automatically' },
      { title: 'Readability Scoring', desc: 'Flesch-Kincaid grade level — the best titles read at 6th-8th grade' },
      { title: 'Power Word Detection', desc: 'Identify emotional trigger words that drive clicks' },
      { title: 'AI Title Generator', desc: 'Get click-worthy title alternatives in 3 styles' },
      { title: 'Sentiment Analysis', desc: 'Understand emotional tone, best quotes, and hook ideas' },
      { title: 'SEO Tag Generator', desc: 'Auto-generate optimized tags and descriptions' },
    ],
  },
  {
    id: 'simulator',
    label: 'CTR Simulator',
    heading: 'Test titles before the algorithm does',
    desc: 'Run your titles through a sandbox of 100,000 synthetic viewers across 9 viewer archetypes. See predicted CTR, compare up to 3 titles, and get AI analysis.',
    link: '/ctr-simulator/',
    appLink: '/#/simulator',
    ctaText: 'Run a Simulation',
    steps: [
      { title: 'Enter up to 3 titles', desc: 'Type your title ideas or variations you\'re considering. Compare them side-by-side.' },
      { title: 'Simulate 100K viewers', desc: 'Your titles are tested against 9 viewer archetypes across 100,000 simulated impressions.' },
      { title: 'Pick the winner', desc: 'See predicted CTR, strengths, weaknesses, and AI-suggested rewrites to maximize clicks.' },
    ],
    points: [
      { title: '100K Synthetic Viewers', desc: '10 rounds of sandbox simulation, averaged for reliability' },
      { title: '9 Viewer Archetypes', desc: 'From Casual Scrollers to Deep Divers — each clicks differently' },
      { title: 'Compare 3 Titles', desc: 'A/B/C test your ideas with win counts and confidence intervals' },
      { title: '18 Signal Dimensions', desc: 'AI scores curiosity, urgency, emotion, narrative, and more' },
      { title: 'Strengths & Weaknesses', desc: 'See exactly what works and what to improve' },
      { title: 'AI Rewrite Suggestions', desc: 'Get an improved title that addresses your weaknesses' },
    ],
  },
];

const StepArrow = () => (
  <div style={styles.stepArrow} className="landing-step-arrow">
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4b5563" strokeWidth="2">
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  </div>
);

const FeatureTabs = ({ onSignIn, activeTab, setActiveTab }) => {
  const tab = FEATURE_TABS_DATA[activeTab];

  return (
    <div>
      {/* Tab toggle */}
      <div style={ftStyles.tabRow}>
        {FEATURE_TABS_DATA.map((t, i) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(i)}
            style={i === activeTab ? ftStyles.tabActive : ftStyles.tab}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div style={ftStyles.content}>
        <h3 style={ftStyles.heading}>{tab.heading}</h3>
        <p style={ftStyles.desc}>{tab.desc}</p>

        {/* How it works steps */}
        <RevealGroup className="landing-steps-row" style={ftStyles.stepsRow} key={`steps-${tab.id}`}>
          {tab.steps.map((s, i) => (
            <div key={i} style={{ display: 'contents' }}>
              {i > 0 && <StepArrow />}
              <StepCard number={String(i + 1)} title={s.title} desc={s.desc} />
            </div>
          ))}
        </RevealGroup>

        {/* Divider */}
        <div style={ftStyles.divider}>
          <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.08)' }} />
          <span style={ftStyles.dividerLabel}>Deep dive</span>
          <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.08)' }} />
        </div>

        {/* Detailed feature points */}
        <div className="landing-features-grid" style={ftStyles.pointsGrid}>
          {tab.points.map((p, i) => (
            <div key={i} style={ftStyles.pointCard} className="landing-feature-card">
              <div style={ftStyles.pointNum}>{i + 1}</div>
              <h4 style={ftStyles.pointTitle}>{p.title}</h4>
              <p style={ftStyles.pointDesc}>{p.desc}</p>
            </div>
          ))}
        </div>

        <div style={ftStyles.ctaRow}>
          <button onClick={onSignIn} style={ftStyles.ctaBtn} className="landing-cta-btn">
            {tab.ctaText}
          </button>
          <a href="/#/features" style={ftStyles.learnLink}>
            Learn more
            <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z"/></svg>
          </a>
        </div>
      </div>
    </div>
  );
};

const SimDemoSection = ({ visible }) => {
  const best = SIM_DEMO_DATA.titles[0];
  return (
    <>
      {/* CTR comparison table */}
      <div style={demoStyles.card}>
        <h4 style={demoStyles.cardTitle}>CTR Comparison</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {SIM_DEMO_DATA.titles.map((t, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: '12px',
              padding: '12px 14px', borderRadius: '10px',
              background: i === 0 ? 'rgba(68,221,102,0.06)' : 'rgba(255,255,255,0.02)',
              border: i === 0 ? '1px solid rgba(68,221,102,0.2)' : '1px solid rgba(255,255,255,0.06)',
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(12px)',
              transition: `opacity 0.5s ease ${0.2 + i * 0.15}s, transform 0.5s ease ${0.2 + i * 0.15}s`,
            }}>
              <span style={{
                width: '22px', height: '22px', borderRadius: '6px', fontSize: '11px', fontWeight: '800',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                background: i === 0 ? 'rgba(68,221,102,0.15)' : 'rgba(255,255,255,0.06)',
                color: i === 0 ? '#44dd66' : '#64748b',
              }}>{i + 1}</span>
              <span style={{ flex: 1, fontSize: '13px', fontWeight: '600', color: '#e2e8f0', minWidth: 0 }}>{t.title}</span>
              <span style={{ fontSize: '16px', fontWeight: '800', color: t.color, flexShrink: 0 }}>{t.ctr}%</span>
              <span style={{
                fontSize: '10px', fontWeight: '600', padding: '2px 8px', borderRadius: '6px', flexShrink: 0,
                background: `${t.color}15`, color: t.color,
              }}>{t.rating}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Grid: Archetypes + Strengths/Weaknesses */}
      <div className="landing-demo-grid" style={styles.demoGrid}>
        {/* Archetype breakdown */}
        <div style={demoStyles.card}>
          <h4 style={demoStyles.cardTitle}>Per-Archetype CTR</h4>
          <div style={{ fontSize: '11px', color: '#475569', display: 'flex', gap: '4px', marginBottom: '12px' }}>
            <span style={{ flex: 1 }} />
            <span style={{ width: '50px', textAlign: 'right', fontWeight: '600' }}>Title 1</span>
            <span style={{ width: '50px', textAlign: 'right', fontWeight: '600' }}>Title 2</span>
            <span style={{ width: '50px', textAlign: 'right', fontWeight: '600' }}>Title 3</span>
          </div>
          {SIM_DEMO_DATA.archetypes.map((a, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: '4px', padding: '8px 0',
              borderBottom: i < SIM_DEMO_DATA.archetypes.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
              opacity: visible ? 1 : 0, transition: `opacity 0.5s ease ${0.3 + i * 0.1}s`,
            }}>
              <span style={{ flex: 1, fontSize: '12px', color: '#94a3b8', fontWeight: '500' }}>{a.name}</span>
              <span style={{ width: '50px', textAlign: 'right', fontSize: '12px', fontWeight: '700', color: '#44dd66', fontFamily: 'monospace' }}>{a.ctr1}%</span>
              <span style={{ width: '50px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#94a3b8', fontFamily: 'monospace' }}>{a.ctr2}%</span>
              <span style={{ width: '50px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#94a3b8', fontFamily: 'monospace' }}>{a.ctr3}%</span>
            </div>
          ))}
        </div>

        {/* Strengths & Weaknesses */}
        <div style={demoStyles.card}>
          <h4 style={demoStyles.cardTitle}>Strengths & Weaknesses</h4>
          <p style={{ margin: '0 0 10px', fontSize: '11px', color: '#475569', fontWeight: '600' }}>"{best.title}"</p>
          {SIM_DEMO_DATA.strengths.map((s, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px',
              opacity: visible ? 1 : 0, transition: `opacity 0.5s ease ${0.4 + i * 0.1}s`,
            }}>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="#4ade80" style={{ flexShrink: 0 }}>
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
              <span style={{ fontSize: '13px', color: '#e2e8f0' }}>{s}</span>
              <div style={{ flex: 1, height: '4px', borderRadius: '2px', background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: '2px', background: '#4ade80',
                  width: visible ? `${75 + i * 5}%` : '0%',
                  transition: `width 0.8s ease ${0.5 + i * 0.1}s`,
                }} />
              </div>
            </div>
          ))}
          <div style={{ height: '12px' }} />
          {SIM_DEMO_DATA.weaknesses.map((w, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px',
              opacity: visible ? 1 : 0, transition: `opacity 0.5s ease ${0.7 + i * 0.1}s`,
            }}>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="#f87171" style={{ flexShrink: 0 }}>
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
              <span style={{ fontSize: '13px', color: '#e2e8f0' }}>{w}</span>
              <div style={{ flex: 1, height: '4px', borderRadius: '2px', background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: '2px', background: '#f87171',
                  width: visible ? `${30 - i * 5}%` : '0%',
                  transition: `width 0.8s ease ${0.8 + i * 0.1}s`,
                }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* AI rewrite */}
      <div style={{ ...demoStyles.aiCard, marginTop: '16px' }}>
        <div style={demoStyles.aiLabel}>AI</div>
        <h4 style={demoStyles.cardTitle}>Suggested Rewrite</h4>
        <div style={{
          ...demoStyles.titleIdea,
          opacity: visible ? 1 : 0, transition: 'opacity 0.6s ease 0.8s',
        }}>
          I Was Buried Alive for 50 Hours and Almost Didn't Make It
          <span style={demoStyles.titleLen}>56 chars</span>
        </div>
        <p style={{ margin: '10px 0 0', fontSize: '12px', color: '#94a3b8', lineHeight: '1.6' }}>
          Adds personal stakes and emotional tension while keeping the specificity that drives curiosity.
        </p>
      </div>
    </>
  );
};

const ftStyles = {
  tabRow: {
    display: 'flex', gap: '4px', marginBottom: '28px',
    background: 'rgba(255,255,255,0.04)', borderRadius: '12px', padding: '4px',
    maxWidth: '400px', margin: '0 auto 28px',
  },
  tab: {
    flex: 1, padding: '10px 20px', borderRadius: '10px', border: 'none',
    background: 'transparent', color: '#64748b', fontSize: '14px', fontWeight: '600',
    cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s',
  },
  tabActive: {
    flex: 1, padding: '10px 20px', borderRadius: '10px', border: 'none',
    background: 'rgba(167,139,250,0.15)', color: '#a78bfa', fontSize: '14px', fontWeight: '600',
    cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s',
  },
  content: {
    background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: '18px', padding: '36px 32px',
  },
  heading: {
    fontSize: '24px', fontWeight: '800', color: '#e2e8f0', letterSpacing: '-0.5px',
    marginBottom: '10px',
  },
  desc: {
    fontSize: '15px', color: '#94a3b8', lineHeight: '1.7', marginBottom: '28px',
    maxWidth: '600px',
  },
  stepsRow: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    gap: '20px', marginBottom: '0',
  },
  divider: {
    display: 'flex', alignItems: 'center', gap: '16px', margin: '36px 0 28px',
  },
  dividerLabel: {
    fontSize: '11px', fontWeight: '700', color: '#475569',
    textTransform: 'uppercase', letterSpacing: '1px', whiteSpace: 'nowrap',
    padding: '0 4px',
  },
  pointsGrid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px',
    marginBottom: '28px',
  },
  pointCard: {
    background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: '12px', padding: '18px',
    transition: 'transform 0.2s, border-color 0.2s',
  },
  pointNum: {
    width: '24px', height: '24px', borderRadius: '7px',
    background: 'rgba(124,58,237,0.12)', color: '#a78bfa',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '11px', fontWeight: '800', marginBottom: '10px',
  },
  pointTitle: {
    fontSize: '14px', fontWeight: '700', color: '#e2e8f0', marginBottom: '4px',
  },
  pointDesc: {
    fontSize: '12px', color: '#94a3b8', lineHeight: '1.5',
  },
  ctaRow: {
    display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap',
  },
  ctaBtn: {
    display: 'inline-block', padding: '12px 28px', borderRadius: '12px', border: 'none',
    background: 'linear-gradient(135deg, #7c3aed, #6366f1)',
    color: '#fff', fontSize: '14px', fontWeight: '700',
    textDecoration: 'none', transition: 'transform 0.2s, box-shadow 0.2s',
  },
  learnLink: {
    display: 'inline-flex', alignItems: 'center', gap: '4px',
    fontSize: '13px', fontWeight: '600', color: '#a78bfa',
    textDecoration: 'none',
  },
};

// === STYLES ===

const styles = {
  page: {
    minHeight: '100vh',
    background: '#000000',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: '#e2e8f0',
    position: 'relative',
    overflow: 'clip',
  },
  // Floating orbs
  orb1: {
    position: 'absolute', top: '-10%', right: '-5%',
    width: '600px', height: '600px', borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(124,58,237,0.15) 0%, transparent 70%)',
    animation: 'float1 20s ease-in-out infinite',
    pointerEvents: 'none', zIndex: 0,
    willChange: 'transform',
  },
  orb2: {
    position: 'absolute', top: '60%', left: '-10%',
    width: '500px', height: '500px', borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(99,102,241,0.12) 0%, transparent 70%)',
    animation: 'float2 25s ease-in-out infinite',
    pointerEvents: 'none', zIndex: 0,
    willChange: 'transform',
  },
  orb3: {
    position: 'absolute', top: '140%', left: '50%',
    width: '400px', height: '400px', borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(16,185,129,0.08) 0%, transparent 70%)',
    animation: 'float3 18s ease-in-out infinite',
    pointerEvents: 'none', zIndex: 0,
    willChange: 'transform',
  },
  // Hero
  hero: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', minHeight: '100vh', padding: '80px 24px 60px',
    textAlign: 'center', position: 'relative', zIndex: 1,
  },
  heroTitle: {
    fontSize: '56px', fontWeight: '800', marginBottom: '24px', marginTop: 0,
    letterSpacing: '-2.5px', maxWidth: '750px', lineHeight: '1.1',
    background: 'linear-gradient(135deg, #e2e8f0 0%, #a78bfa 50%, #818cf8 100%)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  },
  heroSub: {
    fontSize: '20px', color: '#94a3b8', lineHeight: '1.7',
    maxWidth: '540px', margin: '0 0 40px', fontWeight: '400',
  },
  ctaButton: {
    padding: '16px 48px', border: 'none', borderRadius: '14px',
    background: 'linear-gradient(135deg, #7c3aed, #6366f1)',
    color: '#fff', fontSize: '17px', fontWeight: '600',
    cursor: 'pointer', fontFamily: 'inherit',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    boxShadow: '0 4px 24px rgba(99, 102, 241, 0.25)',
  },
  ctaHint: {
    marginTop: '16px', fontSize: '13px', color: '#475569',
  },
  // Stats
  statsRow: {
    display: 'flex', gap: '16px', marginTop: '64px',
  },
  statCard: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '16px', padding: '24px 32px',
    textAlign: 'center', backdropFilter: 'blur(12px)',
    transition: 'transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease',
    cursor: 'default',
  },
  statValue: {
    display: 'block', fontSize: '32px', fontWeight: '800',
    background: 'linear-gradient(135deg, #a78bfa, #6366f1)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  },
  statLabel: {
    display: 'block', fontSize: '13px', color: '#64748b', marginTop: '6px',
  },
  // Section
  section: {
    maxWidth: '1060px', margin: '0 auto', padding: '100px 24px',
    position: 'relative', zIndex: 1,
  },
  sectionHeading: {
    fontSize: '40px', fontWeight: '800', textAlign: 'center',
    marginBottom: '12px', marginTop: 0, letterSpacing: '-1.5px',
    background: 'linear-gradient(135deg, #e2e8f0, #a78bfa)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  },
  sectionSub: {
    textAlign: 'center', color: '#64748b', fontSize: '16px',
    marginBottom: 0, marginTop: '8px', lineHeight: '1.6',
  },
  // Steps
  stepsRow: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    gap: '20px',
  },
  step: {
    flex: 1, maxWidth: '260px', textAlign: 'center',
  },
  stepNumber: {
    width: '52px', height: '52px', borderRadius: '16px',
    background: 'linear-gradient(135deg, #7c3aed, #6366f1)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: '0 auto 18px', fontSize: '20px', fontWeight: '700', color: '#fff',
  },
  stepTitle: {
    margin: '0 0 8px', fontSize: '18px', fontWeight: '700', color: '#e2e8f0',
  },
  stepDesc: {
    margin: 0, fontSize: '14px', color: '#94a3b8', lineHeight: '1.6',
  },
  stepArrow: {
    paddingTop: '14px', flexShrink: 0,
  },
  // Demo
  demoContainer: {
    maxWidth: '900px', margin: '0 auto',
  },
  demoVideoCard: {
    display: 'flex', gap: '16px', alignItems: 'center',
    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '16px', padding: '20px',
  },
  demoThumbPlaceholder: {
    width: '140px', height: '80px', borderRadius: '10px', flexShrink: 0,
    background: 'linear-gradient(135deg, #1e1b4b, #312e81)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  demoVideoTitle: {
    margin: '0 0 8px', fontSize: '16px', fontWeight: '600', color: '#e2e8f0',
  },
  demoVideoStats: {
    display: 'flex', gap: '16px', fontSize: '13px', color: '#64748b',
  },
  demoGrid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px',
  },
  toolkitGroupTitle: {
    display: 'flex', alignItems: 'center', gap: '10px',
    fontSize: '20px', fontWeight: '700', color: '#e2e8f0',
    margin: '0 0 4px', letterSpacing: '-0.5px',
  },
  // Features grid
  featuresGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px',
  },
  featureCard: {
    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '16px', padding: '28px 24px',
    transition: 'transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease',
    cursor: 'default', position: 'relative',
  },
  featureCardAi: {
    background: 'rgba(16, 185, 129, 0.04)', border: '1px solid rgba(16, 185, 129, 0.15)',
    borderRadius: '16px', padding: '28px 24px',
    transition: 'transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease',
    cursor: 'default', position: 'relative',
  },
  featureIconWrap: {
    width: '44px', height: '44px', borderRadius: '12px',
    background: 'rgba(124,58,237,0.12)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    marginBottom: '16px',
  },
  featureIconWrapAi: {
    width: '44px', height: '44px', borderRadius: '12px',
    background: 'rgba(16,185,129,0.12)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    marginBottom: '16px',
  },
  proBadge: {
    position: 'absolute', top: '16px', right: '16px',
    padding: '2px 8px', borderRadius: '6px',
    background: 'rgba(16,185,129,0.15)', color: '#6ee7b7',
    fontSize: '10px', fontWeight: '700', letterSpacing: '0.5px',
  },
  featureTitle: {
    margin: '0 0 6px', fontSize: '15px', fontWeight: '600', color: '#e2e8f0',
  },
  featureTitleAi: {
    margin: '0 0 6px', fontSize: '15px', fontWeight: '600', color: '#6ee7b7',
  },
  featureDesc: {
    margin: 0, fontSize: '13px', color: '#64748b', lineHeight: '1.6',
  },
  // Why section
  whyGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px',
  },
  whyCard: {
    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '16px', padding: '32px 28px',
    textAlign: 'center',
    transition: 'transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease',
    cursor: 'default',
  },
  whyIcon: {
    marginBottom: '16px',
  },
  whyTitle: {
    margin: '0 0 10px', fontSize: '18px', fontWeight: '700', color: '#e2e8f0',
  },
  whyDesc: {
    margin: 0, fontSize: '14px', color: '#94a3b8', lineHeight: '1.7',
  },
  // Final CTA
  finalCta: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    padding: '100px 24px', textAlign: 'center', position: 'relative', zIndex: 1,
  },
  finalCtaTitle: {
    fontSize: '36px', fontWeight: '800', marginBottom: '12px', marginTop: 0,
    letterSpacing: '-1px', color: '#e2e8f0',
  },
  finalCtaSub: {
    fontSize: '16px', color: '#94a3b8', marginBottom: '32px', marginTop: 0,
    maxWidth: '500px', lineHeight: '1.6',
  },
  // Footer
  footer: {
    textAlign: 'center', padding: '24px',
    borderTop: '1px solid rgba(255,255,255,0.06)',
    position: 'relative', zIndex: 1,
  },
  footerText: {
    fontSize: '13px', color: '#333',
  },
};

const demoStyles = {
  card: {
    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '16px', padding: '24px',
  },
  aiCard: {
    background: 'rgba(16, 185, 129, 0.04)', border: '1px solid rgba(16, 185, 129, 0.15)',
    borderRadius: '16px', padding: '24px', position: 'relative',
  },
  aiLabel: {
    position: 'absolute', top: '14px', right: '14px',
    fontSize: '10px', fontWeight: '700', color: '#10b981',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    background: 'rgba(16, 185, 129, 0.15)', padding: '3px 8px', borderRadius: '6px',
  },
  cardTitle: {
    margin: '0 0 14px', fontSize: '14px', fontWeight: '700', color: '#94a3b8',
    textTransform: 'uppercase', letterSpacing: '0.5px',
  },
  barRow: {
    display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px',
  },
  barWord: {
    width: '100px', fontSize: '13px', color: '#cbd5e1', fontWeight: '500',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  barTrack: {
    flex: 1, height: '20px', backgroundColor: 'rgba(255,255,255,0.06)',
    borderRadius: '6px', overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #7c3aed, #818cf8)',
    borderRadius: '6px',
  },
  barCount: {
    width: '28px', textAlign: 'right', fontSize: '12px', color: '#64748b',
    fontFamily: 'monospace', fontWeight: '600',
  },
  sentimentRow: {
    display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
  },
  sentimentChip: {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    background: 'rgba(255,255,255,0.06)', borderRadius: '20px', padding: '6px 14px',
    fontSize: '13px',
  },
  toneChip: {
    fontSize: '12px', color: '#94a3b8', fontStyle: 'italic',
    background: 'rgba(255,255,255,0.04)', padding: '4px 12px', borderRadius: '12px',
  },
  energyChip: {
    fontSize: '12px', color: '#fbbf24',
    background: 'rgba(251,191,36,0.08)', padding: '4px 12px', borderRadius: '12px',
  },
  meterTrack: {
    width: '100%', height: '6px', borderRadius: '3px',
    background: 'rgba(255,255,255,0.08)', overflow: 'hidden',
  },
  meterLabels: {
    display: 'flex', justifyContent: 'space-between',
    fontSize: '10px', color: '#475569', marginTop: '6px',
  },
  sentimentSummary: {
    margin: '14px 0 0', fontSize: '13px', color: '#94a3b8', lineHeight: '1.6',
  },
  aiQuote: {
    color: '#6ee7b7', fontSize: '13px', lineHeight: '1.6',
    fontStyle: 'italic', padding: '10px 14px', marginBottom: '6px',
    background: 'rgba(16, 185, 129, 0.06)', borderRadius: '8px',
    borderLeft: '3px solid rgba(16, 185, 129, 0.3)',
  },
  titleIdea: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '10px 14px', marginBottom: '8px',
    background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.15)',
    borderRadius: '10px', fontSize: '13px', color: '#c4b5fd',
  },
  titleLen: {
    fontSize: '11px', color: '#4ade80', marginLeft: '8px', flexShrink: 0,
  },
};

export default LandingPage;
